import React, { useState } from "react"
import { Button, Grid, Typography } from "@material-ui/core"

import useStyles from "@/components/Messages/CustomLinkMessage/styles"
import { BUTTON_MESSAGE_WIDTH } from "@/components/Messages/ButtonMessage/styles"

import ActionDialog from "@/components/ActionDialog"

import { IMessageExtraData } from "@/protocols/channel"
import { useChatGlobalStateStore } from "@/store/ChatGlobalState"

import {
	Reply,
	Phone,
	Launch
} from "@material-ui/icons"

type ButtonType = NonNullable<NonNullable<IMessageExtraData["customLink"]>["buttons"]>[number]

type ButtonItemProps = {
	button: ButtonType
	onClick: () => void
	sentByCustomer?: boolean
	status?: string
	isDefaultSize?: boolean
	style?: React.CSSProperties
}

const ButtonItem: React.FC<ButtonItemProps> = (props) => {
	const {
		button,
		onClick,
		isDefaultSize = true,
		style
	} = props

	const classes = useStyles()

	const getButtonIcon = (buttonType?: ButtonType["type"]): JSX.Element | null => {
		const buttonTypeToButtonIcon: Record<NonNullable<ButtonType["type"]>, JSX.Element> = {
			quickReply: <Reply />,
			link: <Launch />,
			call: <Phone />
		}

		if (!buttonType) {
			return null
		}

		return buttonTypeToButtonIcon[buttonType] || null
	}

	const displayText = button.displayText

	return (
		<Button
			variant="contained"
			fullWidth
			className={classes.button}
			style={{
				...(isDefaultSize && { width: BUTTON_MESSAGE_WIDTH }),
				...style
			}}
			classes={{
				label: classes.buttonText
			}}
			startIcon={getButtonIcon(button.type)}
			onClick={onClick}
		>
			{displayText}
		</Button>
	)
}

type ButtonProps = {
	button: ButtonType
	messageId: string
	style?: React.CSSProperties
}

const QuickReplyButton: React.FC<ButtonProps> = (props) => {
	const { button, messageId: replyToMessageId, style } = props
	const chatGlobalStateStore = useChatGlobalStateStore()

	const handleOnClickQuickReply = (displayText?: string) => {
		if (!displayText) {
			return
		}

		chatGlobalStateStore.conversationPanel.textInput.sendText?.(displayText, replyToMessageId)
	}

	return (
		<ButtonItem
			button={button}
			onClick={() => handleOnClickQuickReply(button.displayText)}
			style={style}
		/>
	)
}

const LinkButton: React.FC<ButtonProps> = (props) => {
	const { button, style } = props
	const [showMenuDialogOpened, setShowMenuDialogOpened] = useState(false)
	const [selectedUrl, setSelectedUrl] = useState("")

	const handleOpenShowMenuDialog = (url: string) => {
		setSelectedUrl(url)
		setShowMenuDialogOpened(true)
	}

	const handleCloseShowMenuDialog = () => {
		setShowMenuDialogOpened(false)
	}

	const handleClickToNext = () => {
		window.open(selectedUrl, "_blank")
		handleCloseShowMenuDialog()
	}

	return (
		<>
			<ButtonItem
				button={button}
				onClick={() => handleOpenShowMenuDialog(button.url)}
				style={style}
			/>
			<ActionDialog
				title={"LINK EXTERNO"}
				onClose={handleCloseShowMenuDialog}
				saveText="PROSSEGUIR"
				onSave={handleClickToNext}
				openDialog={showMenuDialogOpened}
				maxWidth="sm"
				fullWidth
			>
				<Typography>
					Você será redirecionado para fora da Letalk, deseja continuar? <br />
					Destino: <b>{selectedUrl}</b>
				</Typography>
			</ActionDialog>
		</>
	)
}

const buttonTypeToButtonComponent = (type?: ButtonType["type"]): React.FC<ButtonProps> | null => {
	const typeToComponent: Partial<Record<NonNullable<ButtonType["type"]>, React.FC<ButtonProps>>> = {
		quickReply: QuickReplyButton,
		link: LinkButton
	}

	if (!type) return null

	return typeToComponent[type] || null
}

type CustomLinkMessageProps = {
	messageId?: string
	extraData: IMessageExtraData
	sentByCustomer?: boolean
	isDefaultSize?: boolean
}

export const CustomLinkMessageContentFooter: React.FC<CustomLinkMessageProps> = (props) => {
	const {
		messageId,
		extraData,
		isDefaultSize = true
	} = props

	const buttons = extraData?.customLink?.buttons

	return (
		<>
			{
				buttons?.map((button, index) => {
					const ButtonComponent = buttonTypeToButtonComponent(button?.type)
					if (!ButtonComponent) {
						return null
					}

					return (
						<Grid key={index}>
							<ButtonComponent
								button={button}
								messageId={messageId || ""}
								style={{
									marginBottom: 8,
									...(isDefaultSize && { width: BUTTON_MESSAGE_WIDTH })
								}}
							/>
						</Grid>
					)
				})
			}
		</>
	)
}
